import React, { useState, useEffect, useCallback } from 'react';
import { linkifyText } from '../../utils/linkUtils';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Badge,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  TablePagination,
  Alert,
  useMediaQuery,
  Menu,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
  Delete as DeleteIcon,
  CheckCircle as ResolvedIcon,
  Schedule as PendingIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  CalendarToday as DateIcon,
  Close as CloseIcon,
  Message as MessageIcon,
  FilterList as FilterIcon,
  RestartAlt as RestartIcon,
  Support as SupportIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  RadioButtonChecked as CheckedIcon,
  Check as CheckIcon,
  Remove as MinusIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../utils/apiUrl';
import { useHeartbeat } from '../../contexts/HeartbeatContext';
import SuccessDialog from '../../components/SuccessDialog';
import './admin-css/SupportInquiries-light.css';
import './admin-css/SupportInquiries-dark.css';

interface SupportTicket {
  _id: string;
  name: string;
  email: string;
  client?: {
    _id: string;
    clientName: string;
    companyName: string;
    username: string;
  };
  inquiryType: 'technical_issue' | 'deactivated_account' | 'reset-password' | 'signup' | 'other';
  subject: string;
  message: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  isFromDeactivatedAccount: boolean;
  metadata?: {
    accountType?: string;
    requestedFeatures?: string[];
    urgency?: string;
    username?: string; // Added for reject dialog
    email?: string; // Added for reset password
  };
  createdAt: string;
  updatedAt: string;
}

const SupportInquiries: React.FC = () => {
  const theme = useTheme();
  const { lastHeartbeat } = useHeartbeat();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState<Date | null>(null);
  const [dateToFilter, setDateToFilter] = useState<Date | null>(null);
  const [selectedTickets, setSelectedTickets] = useState<string[]>([]);
  const [showIndeterminate, setShowIndeterminate] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<SupportTicket | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  
  // Mobile states
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedTicketForMenu, setSelectedTicketForMenu] = useState<SupportTicket | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedTicketForReject, setSelectedTicketForReject] = useState<SupportTicket | null>(null);
  
  // Success Dialog state
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState({
    title: '',
    message: '',
    autoCloseDelay: 3000,
  });
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${getApiUrl()}/api/support/tickets/admin`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTickets(response.data);
    } catch (error: any) {
      setSuccessDialogConfig({
        title: 'Error Loading Tickets',
        message: error.response?.data?.message || 'Failed to fetch support tickets',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Auto-refresh tickets every 15 seconds to match heartbeat interval
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchTickets();
    }, 15000);

    return () => clearInterval(intervalId);
  }, [fetchTickets]);

  // Refresh when heartbeat updates
  useEffect(() => {
    if (lastHeartbeat) {
      fetchTickets();
    }
  }, [lastHeartbeat, fetchTickets]);

  // Filter and sort tickets
  useEffect(() => {
    let filtered = [...tickets];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(ticket =>
        ticket.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.message.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(ticket => ticket.status === statusFilter);
    }

    // Apply date filter
    if (dateFromFilter || dateToFilter) {
      filtered = filtered.filter(ticket => {
        const ticketDate = new Date(ticket.createdAt);
        
        if (dateFromFilter && dateToFilter) {
          // Both dates selected - filter between range
          const fromDate = new Date(dateFromFilter);
          fromDate.setHours(0, 0, 0, 0);
          const toDate = new Date(dateToFilter);
          toDate.setHours(23, 59, 59, 999);
          return ticketDate >= fromDate && ticketDate <= toDate;
        } else if (dateFromFilter) {
          // Only from date selected
          const fromDate = new Date(dateFromFilter);
          fromDate.setHours(0, 0, 0, 0);
          return ticketDate >= fromDate;
        } else if (dateToFilter) {
          // Only to date selected
          const toDate = new Date(dateToFilter);
          toDate.setHours(23, 59, 59, 999);
          return ticketDate <= toDate;
        }
        return true;
      });
    }

    setFilteredTickets(filtered);
    setCurrentPage(0); // Reset to first page when filters change
  }, [tickets, searchQuery, statusFilter, dateFromFilter, dateToFilter]);

  // Pagination logic
  const totalPages = Math.ceil(filteredTickets.length / rowsPerPage);
  const startIndex = currentPage * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedTickets = filteredTickets.slice(startIndex, endIndex);

  const handleChangePage = (event: unknown, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleStatusUpdate = async (ticketId: string, newStatus: string) => {
    try {
      const token = sessionStorage.getItem('token');
      await axios.patch(
        `${getApiUrl()}/api/support/tickets/${ticketId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTickets();
      setSuccessDialogConfig({
        title: 'Status Updated Successfully',
        message: 'Ticket status updated successfully',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } catch (error: any) {
      setSuccessDialogConfig({
        title: 'Error Updating Status',
        message: error.response?.data?.message || 'Failed to update ticket status',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  };

  const handleApprovePasswordReset = async (ticketId: string) => {
    try {
      const token = sessionStorage.getItem('token');
      await axios.post(
        `${getApiUrl()}/api/support/tickets/${ticketId}/approve-password-reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTickets();
      setSuccessDialogConfig({
        title: 'Password Reset Approved',
        message: 'Password reset approved successfully. User will be notified via email.',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } catch (error: any) {
      setSuccessDialogConfig({
        title: 'Error Approving Reset',
        message: error.response?.data?.message || 'Failed to approve password reset',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  };

  const handleRejectPasswordReset = async () => {
    if (!selectedTicketForReject) return;
    
    try {
      const token = sessionStorage.getItem('token');
      await axios.post(
        `${getApiUrl()}/api/support/tickets/${selectedTicketForReject._id}/reject-password-reset`,
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTickets();
      setRejectDialogOpen(false);
      setRejectReason('');
      setSelectedTicketForReject(null);
      setSuccessDialogConfig({
        title: 'Password Reset Rejected',
        message: 'Password reset rejected successfully. User will be notified via email.',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } catch (error: any) {
      setSuccessDialogConfig({
        title: 'Error Rejecting Reset',
        message: error.response?.data?.message || 'Failed to reject password reset',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  };

  const openRejectDialog = (ticket: SupportTicket) => {
    setSelectedTicketForReject(ticket);
    setRejectDialogOpen(true);
  };

  const handleDeleteTickets = async () => {
    try {
      const token = sessionStorage.getItem('token');
      await Promise.all(
        selectedTickets.map(ticketId =>
          axios.delete(
            `${getApiUrl()}/api/support/tickets/${ticketId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      fetchTickets();
      setSelectedTickets([]);
      setDeleteDialogOpen(false);
      setSuccessDialogConfig({
        title: 'Tickets Deleted Successfully',
        message: `${selectedTickets.length} ticket(s) deleted successfully`,
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } catch (error: any) {
      setSuccessDialogConfig({
        title: 'Error Deleting Tickets',
        message: error.response?.data?.message || 'Failed to delete tickets',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['ID', 'Username', 'Email', 'Type', 'Message', 'Status', 'Date'].join(','),
      ...tickets.map(ticket => [
        ticket._id,
        getUsernameDisplay(ticket),
        ticket.email,
        getInquiryTypeLabel(ticket.inquiryType, ticket),
        `"${ticket.message}"`,
        ticket.status.toUpperCase(),
        new Date(ticket.createdAt).toLocaleDateString(),
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `support_tickets_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return '#f44336';
      case 'in-progress': return '#ff9800';
      case 'resolved': return '#4caf50';
      case 'closed': return '#6c778b';
      default: return '#6c778b';
    }
  };

  const getStatusTextColor = (status: string) => {
    switch (status) {
      case 'open': return '#d32f2f';
      case 'in-progress': return '#f57c00';
      case 'resolved': return '#2e7d32';
      case 'closed': return '#364254';
      default: return '#364254';
    }
  };

  const getStatusBackgroundColor = (status: string) => {
    switch (status) {
      case 'open': return '#ffebee';
      case 'in-progress': return '#fff3e0';
      case 'resolved': return '#e8f5e8';
      case 'closed': return '#f2f4f7';
      default: return '#f2f4f7';
    }
  };

  const getInquiryTypeColor = (type: string) => {
    switch (type) {
      case 'reset-password': return 'success'; // Reset password requests now use signup color
      case 'signup': return 'success';
      case 'deactivated_account': return 'error';
      case 'technical_issue': return 'info';
      default: return 'default';
    }
  };

  // Mobile handlers
  const handleExpandRow = (ticketId: string) => {
    setExpandedRow(expandedRow === ticketId ? null : ticketId);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>, ticket: SupportTicket) => {
    setMobileMenuAnchor(event.currentTarget);
    setSelectedTicketForMenu(ticket);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
    setSelectedTicketForMenu(null);
  };

  const handleMobileDelete = () => {
    if (selectedTicketForMenu) {
      setSelectedTickets([selectedTicketForMenu._id]);
      setDeleteDialogOpen(true);
      handleMobileMenuClose();
    }
  };

  const handleMobileResolve = () => {
    if (selectedTicketForMenu) {
      handleStatusUpdate(selectedTicketForMenu._id, 'resolved');
      handleMobileMenuClose();
    }
  };

  const handleMobileMarkInProgress = () => {
    if (selectedTicketForMenu) {
      handleStatusUpdate(selectedTicketForMenu._id, 'in-progress');
      handleMobileMenuClose();
    }
  };

  const getInquiryTypeLabel = (type: string, ticket: SupportTicket) => {
    switch (type) {
      case 'reset-password': 
        return 'Signup Request'; // Reset password requests are now treated as signup requests
      case 'signup': 
        return 'Signup Request';
      case 'deactivated_account': 
        return 'Reactivation';
      case 'technical_issue': 
        return 'Technical Issue';
      default: 
        return 'Other';
    }
  };

  const getInquiryTypeIcon = (type: string) => {
    switch (type) {
      case 'reset-password': return '👤'; // Reset password requests now use signup icon
      case 'signup': return '👤';
      case 'deactivated_account': return '🔓';
      case 'technical_issue': return '🛠️';
      default: return '📝';
    }
  };

  const handleSelectAllClick = useCallback(() => {
    if (selectedTickets.length === filteredTickets.length) {
      // If all are selected, deselect all
      setSelectedTickets([]);
    } else {
      // If none or some are selected, select all
      setSelectedTickets(filteredTickets.map(ticket => ticket._id));
    }
  }, [selectedTickets.length, filteredTickets]);

  // Force indeterminate state on the checkbox
  useEffect(() => {
    const isIndeterminate = selectedTickets.length > 0 && selectedTickets.length < filteredTickets.length;
    setShowIndeterminate(isIndeterminate);
  }, [selectedTickets.length, filteredTickets.length]);

  const handleSelectRow = useCallback((ticketId: string, checked: boolean) => {
    if (checked) {
      setSelectedTickets(prev => [...prev, ticketId]);
    } else {
      setSelectedTickets(prev => prev.filter(id => id !== ticketId));
    }
  }, []);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setDateFromFilter(null);
    setDateToFilter(null);
  };

  const getUsernameDisplay = (ticket: SupportTicket) => {
    if (ticket.client?.username) {
      return ticket.client.username;
    }
    
    // Check if it's an admin request
    if (ticket.metadata?.accountType === 'admin') {
      return 'Admin Request';
    }
    
    // Check if it's a client request (from custom client login page)
    if (ticket.metadata?.accountType === 'client') {
      return 'Client Request';
    }
    
    // Only show as external if it's truly from a public page
    return `External (${ticket.email.split('@')[0]})`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day} - ${month} - ${year}`;
  };



  const getTicketStats = () => {
    const stats = {
      total: tickets.length,
      open: tickets.filter(t => t.status === 'open').length,
      inProgress: tickets.filter(t => t.status === 'in-progress').length,
      resolved: tickets.filter(t => t.status === 'resolved').length,
    };
    return stats;
  };

  const stats = getTicketStats();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <>
      {/* Page Header - Desktop Only */}
      {!isMobile && (
        <Box className="support-page-header">
          <Typography variant="h2" className={isDarkMode ? 'support-page-title-dark' : 'support-page-title-light'}>
          Support Inquiries
        </Typography>
        </Box>
      )}

      {/* Mobile Header */}
      {isMobile && (
        <Box className={`mobile-header ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
          {/* First Row: Title */}
          <Box className={`mobile-header-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
            <Typography 
              variant="h5" 
              className={`support-inquiries-title ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
            >
              Support Inquiries
            </Typography>
          </Box>
          
          {/* Second Row: Horizontal Scrolling Stats */}
          <Box className={`mobile-stats-scroll-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
            <Box className={`mobile-stats-scroll-content ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
              {/* Total Tickets */}
              <Card className={`mobile-stats-card ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                <CardContent className={`mobile-stats-card-content ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                  <Box className={`mobile-stats-icon-container total ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                    <SupportIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" className={`mobile-stats-value ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                    {stats.total}
                  </Typography>
                  <Typography variant="caption" className={`mobile-stats-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                    Total
                  </Typography>
                </CardContent>
              </Card>

              {/* Open Tickets */}
              <Card className={`mobile-stats-card ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                <CardContent className={`mobile-stats-card-content ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                  <Box className={`mobile-stats-icon-container open ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                    <MessageIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" className={`mobile-stats-value ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                    {stats.open}
                  </Typography>
                  <Typography variant="caption" className={`mobile-stats-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                    Open
                  </Typography>
                </CardContent>
              </Card>

              {/* In Progress Tickets */}
              <Card className={`mobile-stats-card ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                <CardContent className={`mobile-stats-card-content ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                  <Box className={`mobile-stats-icon-container progress ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                    <PendingIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" className={`mobile-stats-value ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                    {stats.inProgress}
                  </Typography>
                  <Typography variant="caption" className={`mobile-stats-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                    In Progress
                  </Typography>
                </CardContent>
              </Card>

              {/* Resolved Tickets */}
              <Card className={`mobile-stats-card ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                <CardContent className={`mobile-stats-card-content ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                  <Box className={`mobile-stats-icon-container resolved ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                    <ResolvedIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" className={`mobile-stats-value ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                    {stats.resolved}
                  </Typography>
                  <Typography variant="caption" className={`mobile-stats-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                    Resolved
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
          
          {/* Third Row: Search and Reset Filter */}
          <Box className={`mobile-search-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
            <TextField
              fullWidth
              placeholder="Search by name, email, subject, or message..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ marginRight: 1 }}
            />
            {(searchQuery || statusFilter || dateFromFilter || dateToFilter) && (
              <Button
                variant="text"
                size="small"
                onClick={clearFilters}
              sx={{
                  color: '#FF4347',
                  fontWeight: 'bold',
                  minWidth: 'auto',
                  padding: '4px 8px'
                }}
              >
                Reset
              </Button>
            )}
          </Box>
        </Box>
      )}

      <Box className={isDarkMode ? 'support-inquiries-container-dark' : 'support-inquiries-container-light'}>
        {/* Desktop Stats Cards Container */}
        {!isMobile && (
        <Box className={isDarkMode ? 'support-stats-container-dark' : 'support-stats-container-light'}>
        
        {/* Stats Cards - Matching Dashboard Style */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={isDarkMode ? 'support-stats-card-dark' : 'support-stats-card-light'}>
              <CardContent className={isDarkMode ? 'support-stats-card-content-dark' : 'support-stats-card-content-light'}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" className={isDarkMode ? 'support-stats-label-dark' : 'support-stats-label-light'}>
                      Total Tickets
                    </Typography>
                    <Typography variant="h4" className={isDarkMode ? 'support-stats-value-dark' : 'support-stats-value-light'}>
                      {stats.total.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box className={`support-stats-icon-container-${isDarkMode ? 'dark' : 'light'} support-stats-icon-total-${isDarkMode ? 'dark' : 'light'}`}>
                    <SupportIcon sx={{ fontSize: 28 }} />
                  </Box>
                </Box>
                <Typography variant="caption" className={isDarkMode ? 'support-stats-description-dark' : 'support-stats-description-light'}>
                  All support inquiries
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 30px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.15)'}`,
                },
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ 
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                      mb: 1
                    }}>
                      Open
                    </Typography>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700, 
                      color: theme.palette.text.primary,
                    }}>
                      {stats.open.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '23px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: stats.open > 0 ? '#f4433620' : '#6c778b20',
                      color: stats.open > 0 ? '#f44336' : '#6c778b',
                    }}
                  >
                    <PendingIcon sx={{ fontSize: 28 }} />
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ 
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem'
                }}>
                  New tickets awaiting response
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 30px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.15)'}`,
                },
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ 
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                      mb: 1
                    }}>
                      In Progress
                    </Typography>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700, 
                      color: theme.palette.text.primary,
                    }}>
                      {stats.inProgress.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '23px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: stats.inProgress > 0 ? '#ff980020' : '#6c778b20',
                      color: stats.inProgress > 0 ? '#ff9800' : '#6c778b',
                    }}
                  >
                    <AssignmentIcon sx={{ fontSize: 28 }} />
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ 
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem'
                }}>
                  Tickets being worked on
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 30px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.15)'}`,
                },
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ 
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                      mb: 1
                    }}>
                      Resolved
                    </Typography>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700, 
                      color: theme.palette.text.primary,
                    }}>
                      {stats.resolved.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '23px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: stats.resolved > 0 ? '#4caf5020' : '#6c778b20',
                      color: stats.resolved > 0 ? '#4caf50' : '#6c778b',
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 28 }} />
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ 
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem'
                }}>
                  Successfully resolved tickets
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </Box>
        )}

        {/* Desktop Top Bar */}
        {!isMobile && (
        <Box className={isDarkMode ? 'support-inquiries-top-bar-dark' : 'support-inquiries-top-bar-light'}>
          {/* Left Side - Action Buttons with Border Radius Container */}
          <Box className={isDarkMode ? 'support-inquiries-action-buttons-dark' : 'support-inquiries-action-buttons-light'}>
            {/* Delete - Always visible with bin icon */}
            <Button
              variant="text"
            size="small"
              onClick={() => setDeleteDialogOpen(true)}
              startIcon={<DeleteIcon />}
              disabled={selectedTickets.length === 0}
              className={isDarkMode ? 'support-inquiries-delete-button-dark' : 'support-inquiries-delete-button-light'}
            >
              Delete
            </Button>

            {/* Filter - Pill Shape */}
          <Button
              variant="text"
              size="small"
              onClick={() => setFilterDialogOpen(true)}
              startIcon={<FilterIcon />}
              className={isDarkMode ? 'support-inquiries-filter-button-dark' : 'support-inquiries-filter-button-light'}
            >
              Filter
          </Button>

            {/* Export */}
          <Button
              variant="text"
              size="small"
            onClick={handleExport}
              startIcon={<ExportIcon />}
              className={isDarkMode ? 'support-inquiries-export-button-dark' : 'support-inquiries-export-button-light'}
          >
            Export
          </Button>

            {/* Clear Filter - Red Button with Restart Icon */}
              <Button
              variant="text"
                color="error"
              size="small"
              onClick={clearFilters}
              startIcon={<RestartIcon />}
              className={isDarkMode ? 'support-inquiries-clear-filter-button-dark' : 'support-inquiries-clear-filter-button-light'}
            >
              Reset Filter
              </Button>
          </Box>

          {/* Right Side - Search and Refresh */}
          <Box className={isDarkMode ? 'support-inquiries-right-container-dark' : 'support-inquiries-right-container-light'}>
            {/* Search Bar - 70% */}
            <Box className={isDarkMode ? 'support-inquiries-search-container-dark' : 'support-inquiries-search-container-light'}>
          <TextField
            fullWidth
            placeholder="Search by name, email, subject, or message..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                      <SearchIcon className={isDarkMode ? 'support-inquiries-search-icon-dark' : 'support-inquiries-search-icon-light'} />
                </InputAdornment>
              ),
          }}
                className={isDarkMode ? 'support-inquiries-search-input-dark' : 'support-inquiries-search-input-light'}
          />
        </Box>
            
            {/* Refresh Button - 30% */}
            <Box className={isDarkMode ? 'support-inquiries-refresh-container-dark' : 'support-inquiries-refresh-container-light'}>
              <Button
                variant="contained"
                size="small"
                onClick={fetchTickets}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} sx={{ color: '#ffffff' }} /> : <RefreshIcon />}
                className={isDarkMode ? 'support-inquiries-refresh-button-dark' : 'support-inquiries-refresh-button-light'}
              >
                Refresh
              </Button>
      </Box>
          </Box>
        </Box>
        )}

      {/* Desktop Data Table */}
      {!isMobile && (
      <Card className={isDarkMode ? 'support-inquiries-table-card-dark' : 'support-inquiries-table-card-light'}>
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow className={isDarkMode ? 'support-inquiries-table-header-dark' : 'support-inquiries-table-header-light'}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedTickets.length === filteredTickets.length && filteredTickets.length > 0}
                    indeterminate={showIndeterminate}
                    onChange={handleSelectAllClick}
                    icon={<UncheckedIcon />}
                    indeterminateIcon={
                      <Box className={isDarkMode ? 'support-inquiries-checkbox-indeterminate-dark' : 'support-inquiries-checkbox-indeterminate-light'}>
                        <MinusIcon className={isDarkMode ? 'support-inquiries-checkbox-icon-dark' : 'support-inquiries-checkbox-icon-light'} />
                      </Box>
                    }
                    checkedIcon={
                      <Box className={isDarkMode ? 'support-inquiries-checkbox-checked-dark' : 'support-inquiries-checkbox-checked-light'}>
                        <MinusIcon className={isDarkMode ? 'support-inquiries-checkbox-icon-dark' : 'support-inquiries-checkbox-icon-light'} />
                      </Box>
                    }
                    className={isDarkMode ? 'support-inquiries-checkbox-dark' : 'support-inquiries-checkbox-light'}
                    title={`Selected: ${selectedTickets.length}, Total: ${filteredTickets.length}, Indeterminate: ${selectedTickets.length > 0 && selectedTickets.length < filteredTickets.length}`}
                  />
                </TableCell>
                <TableCell sx={{ textAlign: 'left' }}>
                  Username
                </TableCell>
                <TableCell sx={{ textAlign: 'left' }}>
                  Email
                </TableCell>
                <TableCell sx={{ textAlign: 'left' }}>
                  Type
                </TableCell>
                <TableCell sx={{ textAlign: 'left' }}>
                  Message
                </TableCell>
                <TableCell sx={{ textAlign: 'left' }}>
                  Status
                </TableCell>
                <TableCell sx={{ textAlign: 'left' }}>
                  Actions
                </TableCell>
                <TableCell sx={{ textAlign: 'left' }}>
                  Date
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTickets.map((ticket, index) => (
                <TableRow
                  key={ticket._id}
                  className={`${isDarkMode ? 'support-inquiries-table-row-dark' : 'support-inquiries-table-row-light'} ${
                    index % 2 === 0 
                      ? (isDarkMode ? 'support-inquiries-table-row-even-dark' : 'support-inquiries-table-row-even-light')
                      : (isDarkMode ? 'support-inquiries-table-row-odd-dark' : 'support-inquiries-table-row-odd-light')
                  }`}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedTickets.includes(ticket._id)}
                      onChange={(e) => handleSelectRow(ticket._id, e.target.checked)}
                      icon={<UncheckedIcon />}
                      checkedIcon={
                        <Box className={isDarkMode ? 'support-inquiries-row-checkbox-checked-dark' : 'support-inquiries-row-checkbox-checked-light'}>
                          <CheckIcon className={isDarkMode ? 'support-inquiries-row-checkbox-check-icon-dark' : 'support-inquiries-row-checkbox-check-icon-light'} />
                        </Box>
                      }
                      className={isDarkMode ? 'support-inquiries-row-checkbox-dark' : 'support-inquiries-row-checkbox-light'}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    <Typography variant="body2" fontWeight="bold" className={`${isDarkMode ? 'support-inquiries-cell-text-primary-dark' : 'support-inquiries-cell-text-primary-light'} ${isDarkMode ? 'support-inquiries-cell-text-bold-dark' : 'support-inquiries-cell-text-bold-light'}`}>
                      {getUsernameDisplay(ticket)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    <Typography variant="body2" className={isDarkMode ? 'support-inquiries-cell-text-primary-dark' : 'support-inquiries-cell-text-primary-light'}>
                      {ticket.email}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    <Typography variant="body2" className={isDarkMode ? 'support-inquiries-cell-text-secondary-dark' : 'support-inquiries-cell-text-secondary-light'}>
                      {getInquiryTypeLabel(ticket.inquiryType, ticket)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    <Typography 
                      variant="body2" 
                      className={isDarkMode ? 'support-inquiries-cell-text-primary-dark' : 'support-inquiries-cell-text-primary-light'}
                      sx={{ 
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': {
                          opacity: 0.8
                        }
                      }}
                      onClick={() => {
                        setSelectedMessage(ticket);
                        setMessageDialogOpen(true);
                      }}
                    >
                      Click to read
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    <Chip
                      label={ticket.status === 'open' ? 'Open' : ticket.status === 'in-progress' ? 'Pending' : ticket.status === 'resolved' ? 'Solved' : ticket.status}
                      size="small"
                      className={
                        ticket.status === 'open' 
                          ? (isDarkMode ? 'support-inquiries-status-chip-open-dark' : 'support-inquiries-status-chip-open-light')
                          : ticket.status === 'in-progress'
                          ? (isDarkMode ? 'support-inquiries-status-chip-progress-dark' : 'support-inquiries-status-chip-progress-light')
                          : ticket.status === 'resolved'
                          ? (isDarkMode ? 'support-inquiries-status-chip-resolved-dark' : 'support-inquiries-status-chip-resolved-light')
                          : (isDarkMode ? 'support-inquiries-status-chip-closed-dark' : 'support-inquiries-status-chip-closed-light')
                      }
                      sx={{
              fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        height: '24px',
                        borderRadius: '12px',
                        '& .MuiChip-label': {
                          padding: '0 8px',
                          paddingLeft: '16px',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: '6px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: getStatusColor(ticket.status),
                        },
                        position: 'relative',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* Green circle with check - Mark as Resolved */}
                      <Tooltip title="Mark as Resolved">
                        <IconButton
                          size="small"
                          onClick={() => handleStatusUpdate(ticket._id, 'resolved')}
                          sx={{ 
                            color: ticket.status === 'resolved' ? '#4caf50' : (isDarkMode ? '#FFFFFF' : '#000000'),
                            backgroundColor: 'transparent',
              '&:hover': {
                              backgroundColor: isDarkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                              color: '#4caf50'
                            }
                          }}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      {/* Red/Orange circle outline - Mark as Pending */}
                      <Tooltip title="Mark as Pending">
                        <IconButton
                          size="small"
                          onClick={() => handleStatusUpdate(ticket._id, 'in-progress')}
                          sx={{ 
                            color: ticket.status === 'in-progress' ? '#ff9800' : (isDarkMode ? '#FFFFFF' : '#000000'),
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                              color: '#ff9800'
                            }
                          }}
                        >
                          <PendingIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    <Typography variant="body2" className={isDarkMode ? 'support-inquiries-cell-text-primary-dark' : 'support-inquiries-cell-text-primary-light'}>
                      {formatDate(ticket.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Box className={isDarkMode ? 'support-inquiries-pagination-container-dark' : 'support-inquiries-pagination-container-light'}>
          <Typography className={isDarkMode ? 'support-inquiries-pagination-info-dark' : 'support-inquiries-pagination-info-light'}>
            Showing {currentPage * rowsPerPage + 1}-{Math.min((currentPage + 1) * rowsPerPage, filteredTickets.length)} of {filteredTickets.length}
          </Typography>
          
          <Box className={isDarkMode ? 'support-inquiries-pagination-controls-dark' : 'support-inquiries-pagination-controls-light'}>
            <IconButton
              onClick={() => handleChangePage(null, Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className={isDarkMode ? 'support-inquiries-pagination-button-dark' : 'support-inquiries-pagination-button-light'}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage >= totalPages - 1}
              className={isDarkMode ? 'support-inquiries-pagination-button-dark' : 'support-inquiries-pagination-button-light'}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
      </Card>
      )}

      {/* Mobile Table */}
      {isMobile && (
        <TableContainer 
          component={Paper} 
          className={`mobile-table-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
        >
          <Table className={`mobile-table ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
            <TableHead>
              <TableRow className={`mobile-table-header ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                <TableCell className={`mobile-table-header-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>Subject</TableCell>
                <TableCell className={`mobile-table-header-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>Status</TableCell>
                <TableCell className={`mobile-table-header-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`}></TableCell>
                <TableCell className={`mobile-table-header-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTickets.map((ticket) => (
                <React.Fragment key={ticket._id}>
                  {/* Mobile Ticket Row */}
                  <TableRow className={`mobile-table-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                    {/* Column 1: Subject */}
                    <TableCell className={`mobile-table-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                      <Typography 
                        variant="body2" 
                        className={`table-subject-name ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
                      >
                        {ticket.subject}
                      </Typography>
                    </TableCell>
                    
                    {/* Column 2: Status */}
                    <TableCell className={`mobile-table-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                      <Chip
                        label={ticket.status}
                        size="small"
                        className={`support-inquiries-status-chip-${ticket.status}-${isDarkMode ? 'dark' : 'light'}`}
          sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    
                    {/* Column 3: Expand/Collapse */}
                    <TableCell className={`mobile-table-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`} align="center">
                      <IconButton
                        size="small"
                        className={`expand-collapse-button ${expandedRow === ticket._id ? 'expanded' : 'collapsed'} ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
                        onClick={() => handleExpandRow(ticket._id)}
                        sx={{
                          backgroundColor: expandedRow === ticket._id ? '#FEE2E2' : '#CCF4CD',
                          color: expandedRow === ticket._id ? '#DC2626' : '#00C805',
              '&:hover': {
                            backgroundColor: expandedRow === ticket._id ? '#FEE2E2' : '#CCF4CD',
                          },
                          width: 32,
                          height: 32,
                        }}
                      >
                        {expandedRow === ticket._id ? (
                          <ExpandLessIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ExpandMoreIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </TableCell>
                    
                    {/* Column 4: Three Dots Menu */}
                    <TableCell className={`mobile-table-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`} align="center">
                      <Box
                        onClick={(e) => handleMobileMenuOpen(e, ticket)}
                        sx={{ cursor: 'pointer', padding: '8px' }}
                      >
                        <MoreVertIcon 
                          className={`three-dots-icon ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
                          sx={{ fontSize: 20 }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Row Content */}
                  <TableRow>
                    <TableCell colSpan={4} sx={{ padding: 0, border: 0 }}>
                      <Collapse in={expandedRow === ticket._id} timeout="auto" unmountOnExit>
                        <Box className={`mobile-ticket-expanded ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                          <Box className={`mobile-ticket-details ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                            <Box className={`mobile-detail-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                              <Typography className={`mobile-detail-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                From:
                              </Typography>
                              <Typography className={`mobile-detail-value ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                {getUsernameDisplay(ticket)}
                              </Typography>
                            </Box>
                            <Box className={`mobile-detail-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                              <Typography className={`mobile-detail-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                Email:
                              </Typography>
                              <Typography className={`mobile-detail-value ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                {ticket.email}
                              </Typography>
                            </Box>
                            <Box className={`mobile-detail-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                              <Typography className={`mobile-detail-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                Type:
                              </Typography>
                              <Typography className={`mobile-detail-value ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                {getInquiryTypeLabel(ticket.inquiryType, ticket)}
                              </Typography>
                            </Box>
                            <Box className={`mobile-detail-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                              <Typography className={`mobile-detail-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                Date:
                              </Typography>
                              <Typography className={`mobile-detail-value ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                {formatDate(ticket.createdAt)}
                              </Typography>
                            </Box>
                            <Box className={`mobile-detail-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                              <Typography className={`mobile-detail-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                Message:
                              </Typography>
                              <Typography className={`mobile-detail-value ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                {ticket.message}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
            border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
          }
        }}
      >
        <MenuItem onClick={handleMobileResolve}>
          <ListItemIcon>
            <ResolvedIcon sx={{ color: isDarkMode ? '#10b981' : '#059669' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Mark as Resolved" 
            sx={{ color: isDarkMode ? '#f1f5f9' : '#1f2937' }}
          />
        </MenuItem>
        <MenuItem onClick={handleMobileMarkInProgress}>
          <ListItemIcon>
            <PendingIcon sx={{ color: isDarkMode ? '#f59e0b' : '#d97706' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Mark as In Progress" 
            sx={{ color: isDarkMode ? '#f1f5f9' : '#1f2937' }}
          />
        </MenuItem>
        <MenuItem onClick={handleMobileDelete}>
          <ListItemIcon>
            <DeleteIcon sx={{ color: isDarkMode ? '#ef4444' : '#dc2626' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Delete Ticket" 
            sx={{ color: isDarkMode ? '#f1f5f9' : '#1f2937' }}
          />
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedTickets.length} ticket(s)? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteTickets} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Message Dialog */}
      <Dialog 
        open={messageDialogOpen} 
        onClose={() => setMessageDialogOpen(false)}
        maxWidth={false}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setMessageDialogOpen(false);
          }
        }}
        PaperProps={{
          sx: {
            width: '720px',
            maxWidth: '90vw',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div">
            Support Inquiry Details
          </Typography>
          <IconButton
            onClick={() => setMessageDialogOpen(false)}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedMessage && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Header Info Row */}
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: 3,
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`
              }}>
                {/* Username */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 140 }}>
                  <PersonIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Username
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedMessage.inquiryType === 'reset-password' && selectedMessage.metadata?.username 
                        ? selectedMessage.metadata.username 
                        : selectedMessage.client?.username || (
                          <Box component="span" sx={{ 
                            color: 'text.secondary',
                            fontStyle: 'italic',
                            fontSize: '0.9rem'
                          }}>
                            External User
                          </Box>
                        )
                      }
                    </Typography>
                  </Box>
                </Box>

                {/* Email */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180, flex: 1 }}>
                  <EmailIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Email
                    </Typography>
                    <Typography variant="body2" fontWeight="medium" sx={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      <span className={isDarkMode ? 'support-inquiries-email-link-dark' : 'support-inquiries-email-link-light'}>
                        {linkifyText(
                          selectedMessage.inquiryType === 'reset-password' && selectedMessage.metadata?.email 
                            ? selectedMessage.metadata.email 
                            : selectedMessage.email,
                          { color: 'primary' }
                        )}
                      </span>
                    </Typography>
                  </Box>
                </Box>

                {/* Date */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                  <DateIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Date
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {new Date(selectedMessage.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>

                {/* Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 100 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5, display: 'block' }}>
                      Status
                    </Typography>
                    <Chip
                      label={selectedMessage.status.toUpperCase()}
                      color={selectedMessage.status === 'resolved' ? 'success' : selectedMessage.status === 'in-progress' ? 'warning' : 'error'}
                      size="small"
                      icon={
                        selectedMessage.status === 'resolved' ? <ResolvedIcon /> :
                        selectedMessage.status === 'in-progress' ? <PendingIcon /> :
                        <CancelIcon />
                      }
                      sx={{ fontSize: '0.75rem', height: '24px' }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Message */}
              <Box>
                <Typography variant="subtitle1" color="text.primary" gutterBottom sx={{ fontWeight: 600, mb: 1.5 }}>
                  Message
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 3, 
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                    maxHeight: 350,
                    overflowY: 'auto',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="body1" sx={{ 
                    whiteSpace: 'pre-wrap', 
                    lineHeight: 1.7,
                    fontSize: '0.95rem'
                  }}>
                    {selectedMessage.message}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageDialogOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog - Custom Implementation */}
      {filterDialogOpen && (
        <Box className={isDarkMode ? 'support-inquiries-custom-dialog-overlay-dark' : 'support-inquiries-custom-dialog-overlay-light'}>
          <Box className={isDarkMode ? 'support-inquiries-custom-dialog-container-dark' : 'support-inquiries-custom-dialog-container-light'}>
            {/* Dialog Header */}
            <Box className={isDarkMode ? 'support-inquiries-custom-dialog-header-dark' : 'support-inquiries-custom-dialog-header-light'}>
              <FilterIcon className={isDarkMode ? 'support-inquiries-dialog-icon-dark' : 'support-inquiries-dialog-icon-light'} />
              <Typography variant="h6" className={isDarkMode ? 'support-inquiries-dialog-title-text-dark' : 'support-inquiries-dialog-title-text-light'}>
          Filter Records
              </Typography>
            </Box>
            
            {/* Dialog Content */}
            <Box className={isDarkMode ? 'support-inquiries-custom-dialog-content-dark' : 'support-inquiries-custom-dialog-content-light'}>
              <Box className={isDarkMode ? 'support-inquiries-filter-form-dark' : 'support-inquiries-filter-form-light'}>
                <Box className={isDarkMode ? 'support-inquiries-date-group-dark' : 'support-inquiries-date-group-light'}>
                  <Typography variant="subtitle2" className={isDarkMode ? 'support-inquiries-filter-label-dark' : 'support-inquiries-filter-label-light'}>
                    Date Range
                  </Typography>
                  <Box className={isDarkMode ? 'support-inquiries-date-inputs-container-dark' : 'support-inquiries-date-inputs-container-light'}>
            <TextField
              fullWidth
              type="date"
              label="From Date"
              value={dateFromFilter ? dateFromFilter.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const dateValue = e.target.value;
                setDateFromFilter(dateValue ? new Date(dateValue) : null);
              }}
              InputLabelProps={{
                shrink: true,
              }}
                      className={isDarkMode ? 'support-inquiries-modern-date-input-dark' : 'support-inquiries-modern-date-input-light'}
            />
            <TextField
              fullWidth
              type="date"
              label="To Date"
              value={dateToFilter ? dateToFilter.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const dateValue = e.target.value;
                setDateToFilter(dateValue ? new Date(dateValue) : null);
              }}
              InputLabelProps={{
                shrink: true,
              }}
                      className={isDarkMode ? 'support-inquiries-modern-date-input-dark' : 'support-inquiries-modern-date-input-light'}
            />
          </Box>
                </Box>
              </Box>
            </Box>
            
            {/* Dialog Actions */}
            <Box className={isDarkMode ? 'support-inquiries-custom-dialog-actions-dark' : 'support-inquiries-custom-dialog-actions-light'}>
              <button 
            onClick={() => setFilterDialogOpen(false)}
                className={isDarkMode ? 'support-inquiries-custom-cancel-button-dark' : 'support-inquiries-custom-cancel-button-light'}
              >
                Cancel
              </button>
              <button 
                onClick={() => setFilterDialogOpen(false)}
                className={isDarkMode ? 'support-inquiries-custom-apply-button-dark' : 'support-inquiries-custom-apply-button-light'}
          >
            Apply Filters
              </button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Reject Password Reset Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Password Reset Request</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to reject the password reset request for{' '}
            <strong>
              {selectedTicketForReject?.inquiryType === 'reset-password' && selectedTicketForReject?.metadata?.username 
                ? selectedTicketForReject.metadata.username 
                : selectedTicketForReject?.metadata?.username || 'Unknown User'
              }
            </strong>?
          </Typography>
          <TextField
            fullWidth
            label="Rejection Reason (Optional)"
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Please provide a reason for rejection..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRejectPasswordReset} color="error" variant="contained">
            Reject Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={successDialogConfig.title}
        message={successDialogConfig.message}
        buttonText="Got it"
        autoCloseDelay={successDialogConfig.autoCloseDelay}
      />
    </Box>
    </>
  );
};

export default SupportInquiries;